import { defineMessages, useIntl } from 'react-intl';
import { AppPricingData } from 'src/contentful/product-catalog';

import Suspense from '@atlaskit/react-ufo/suspense';

import { LoadingSpinner } from '@/components/global-nav/user-profile-new/src/components/spinner';
import {
	PriceFinderMessages,
	ProductPlan,
} from '@/components/wpl/pricing/src/price-finder/constants';
import PriceFinderSection from '@/components/wpl/pricing/src/price-finder/price-finder';
import { PriceFinderContextProvider } from '@/components/wpl/pricing/src/price-finder/price-finder-context.tsx';
import { UserLocale } from '@/utils/useGeoCurrency.ts';

export type TrelloPriceFinderProps = {
	pricing?: AppPricingData;
	countryCode?: UserLocale;
	v2?: boolean;
	shouldAddDiscount?: boolean;
};

export const TrelloPriceFinder = (props: TrelloPriceFinderProps) => {
	const intl = useIntl();

	const freePlanFeatureMessages = [
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.unlimitedCards.title',
				defaultMessage: 'Unlimited cards',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.upTo10BoardsPerWorkspace.title',
				defaultMessage: 'Up to 10 boards per Workspace',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.unlimitedPowerUpsPerBoard.title',
				defaultMessage: 'Unlimited Power-Ups per board',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.unlimitedStorage.title.free',
				defaultMessage: 'Unlimited storage (10MB/file)',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.workspaceCommandRuns.title',
				defaultMessage: '250 Workspace command runs per month',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.customBackgroundsAndStickers.title',
				defaultMessage: 'Custom backgrounds & stickers',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.unlimitedActivityLog.title',
				defaultMessage: 'Unlimited activity log',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.assigneeAndDueDates.title',
				defaultMessage: 'Assignee and due dates',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.mobileApps.title',
				defaultMessage: 'iOS and Android mobile apps',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.twoFactorAuthentication.title',
				defaultMessage: '2-factor authentication',
			}),
		},
	];

	const standardPlanFeatureMessages = [
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.unlimitedBoards.title',
				defaultMessage: 'Unlimited boards',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.advancedChecklists.title',
				defaultMessage: 'Advanced checklists',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.customFields.title',
				defaultMessage: 'Custom Fields',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.unlimitedStorage.title',
				defaultMessage: 'Unlimited storage (250MB/file)',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.workspaceCommandRuns.title',
				defaultMessage: '1,000 Workspace command runs per month',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.singleBoardGuests.title',
				defaultMessage: 'Single board guests',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.savedSearches.title',
				defaultMessage: 'Saved searches',
			}),
		},
	];

	const premiumPlanFeatureMessages = [
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.views.title',
				defaultMessage: 'Views: Calendar, Timeline, Table, Dashboard, and Map',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.workspaceViews.title',
				defaultMessage: 'Workspace views: Table and Calendar',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.unlimitedWorkspaceCommandRuns.title',
				defaultMessage: 'Unlimited Workspace command runs',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.atlassianIntelligence.title',
				defaultMessage: 'Atlassian Intelligence (AI)',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.adminAndSecurityFeatures.title',
				defaultMessage: 'Admin and security features',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.workspaceLevelTemplates.title',
				defaultMessage: 'Workspace-level templates',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.collections.title',
				defaultMessage: 'Collections',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.observers.title',
				defaultMessage: 'Observers',
			}),
		},
		{
			featureMessage: intl.formatMessage({
				id: 'TrelloPricingPage.features.simpleDataExport.title',
				defaultMessage: 'Simple data export',
			}),
		},
	];

	const trelloProductPlans: ProductPlan[] = [
		{
			planName: 'Free',
			offeringSku: 'jira-servicedesk-free',
			recommendationLimit: 0,
			signupLimit: 150000,
			planFeatureMessages: freePlanFeatureMessages,
		},
		{
			planName: 'Standard',
			offeringSku: 'trello-standard-non-profit',
			recommendationLimit: 10,
			signupLimit: 150000,
			planFeatureMessages: standardPlanFeatureMessages,
		},
		{
			planName: 'Premium',
			offeringSku: 'trello-premium-non-profit',
			recommendationLimit: 250,
			signupLimit: 150000,
			planFeatureMessages: premiumPlanFeatureMessages,
		},
	];

	const messages: PriceFinderMessages = defineMessages({
		headerText: {
			id: 'TrelloPricingPage.priceFinder.headerText',
			defaultMessage: 'Build Your <blue>Source of Truth</blue>',
		},
		unitCountInputTextRedesign: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputTextRedesign',
			defaultMessage: 'Team size:',
		},
		unitCountInputTerm: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputTerm',
			defaultMessage: 'users',
		},
		billingCycleText: {
			id: 'ConfluencePricingPage.priceFinder.billingCycleText',
			defaultMessage: 'Billing cycle:',
		},
		unitCountInputText: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputText',
			defaultMessage: 'How many users are on your team?',
		},
		unitCountInputTooltip: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputTooltip',
			defaultMessage:
				'A Confluence user is any user who can log in to Confluence, and who exists in User Management',
		},
		monthlyExampleHelpText: {
			id: 'ConfluencePricingPage.priceFinder.monthlyExampleHelpText',
			defaultMessage:
				'Monthly plans give you flexibility as your number of active users changes, so pay for seats as you use them. Remember that the number of seats may change the total price.',
		},
		annualExampleHelpText: {
			id: 'ConfluencePricingPage.priceFinder.annualExampleHelpText',
			defaultMessage:
				'Annual plans give you flexibility and help you prepare for growth. Buy a group of seats at a flat rate now, then fill your empty seats through the year at no extra cost.',
		},
		exampleStandardPlan: {
			id: 'ConfluencePricingPage.priceFinder.exampleStandardPlan',
			defaultMessage: 'Pricing for {unitCount} users on Standard plan',
			description: 'Standard is a plan name and should not be translated',
		},
		cardOverSignupLimit: {
			id: 'ConfluencePricingPage.priceFinder.cardOverSignupLimit',
			defaultMessage: "Your user count exceeds this plan's limit",
		},
		cardEnterpriseMinimumCount: {
			id: 'ConfluencePricingPage.priceFinder.cardEnterpriseMinimumCount',
			defaultMessage:
				'Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above',
			description: 'Enterprise is a plan name and should not be translated',
		},
		cardUnitCountTierRange: {
			id: 'ConfluencePricingPage.priceFinder.cardUnitCountTierRange',
			defaultMessage: 'User tier: {lowerLimit} - {upperLimit}',
		},
		cardMonthlyPerUnitCount: {
			id: 'TrelloPricingPage.priceFinder.cardMonthlyPerUnitCount',
			defaultMessage: 'per user / month',
		},
		cardFreeDescription: {
			id: 'ConfluencePricingPage.priceFinder.cardFreeDescription',
			defaultMessage: 'Always free for 10 users',
		},
	});
	return (
		<Suspense interactionName={'trello-price-finder'} fallback={LoadingSpinner}>
			<PriceFinderContextProvider
				dataProductKey="trello"
				productPlans={trelloProductPlans}
				defaultUnitCount={150}
				shouldAddDiscount={props.shouldAddDiscount}
				ctaUrlSource="productPlans"
			>
				<PriceFinderSection
					messages={messages}
					serverSideCCPPricing={props.pricing?.ccpPricingData?.trello}
					countryCode={props.countryCode}
				/>
			</PriceFinderContextProvider>
		</Suspense>
	);
};

TrelloPriceFinder.displayName = 'TrelloPriceFinder';
